import { ChangeDetectionStrategy, Component, computed, inject, input, output } from "@angular/core";

import { firstValueFrom, Observable } from "rxjs";

import { DocumentListItemModel } from "@documentation/models/document.model";
import { DocumentationRoutesConstants } from "@documentation/routes/documentation.routes.constants";
import { DocumentExportService } from "@documentation/services/document-export.service";
import { DocumentHttpService } from "@documentation/services/document-http.service";
import { DocumentsStore } from "@documentation/state/documents.store";
import { BaseDocumentListContextMenuComponent } from "@features/documentation/components/sidebar/tabs/documents-list/document-tree/base/base-context-menu.component";
import { NgbDropdownModule } from "@ng-bootstrap/ng-bootstrap";
import { CustomIconComponent } from "@shared/components/custom-icon/custom-icon.component";

export interface DocumentDuplicatePayload {
  doc: DocumentListItemModel;
  title: string;
}

@Component({
  selector: "component-document-context-menu",
  templateUrl: "./document-context-menu.component.html",
  styleUrls: ["./document-context-menu.component.scss"],
  imports: [NgbDropdownModule, CustomIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentContextMenuComponent extends BaseDocumentListContextMenuComponent<DocumentListItemModel> {
  private readonly _documentHttpService = inject(DocumentHttpService);
  private readonly _documentExport = inject(DocumentExportService);
  private readonly _docsStore = inject(DocumentsStore);

  readonly canDrag = input.required<boolean>();
  readonly canMove = input.required<boolean>();

  // Outputs for parent component coordination
  readonly documentDeleted = output<DocumentListItemModel>();
  readonly duplicateDocument = output<DocumentDuplicatePayload>();

  protected override readonly entityType = DocumentListItemModel;

  protected override readonly publicSuccessMessage = "Document is now public";
  protected override readonly privateSuccessMessage = "Document is now private";

  protected readonly canEdit = computed(() => {
    const doc = this.entity();
    return doc && doc.permission !== "VIEW";
  });

  onRenameClick(event: Event): void {
    this.handleOnRenameClick(event, this.entity().title, "Rename Document", "Document Name");
  }

  protected override handleRename(title: string): void {
    try {
      this._documentHttpService.updateDocumentTitle(this.entity().id, title).subscribe({
        next: () => {
          this.entityUpdated.emit(this.getUpdatedModel({ title }));
        }
      });
    } catch (e) {
      this._logger.error("Failed to update document title", e);
      this._toastService.error("Failed to update document title");
    }
  }

  protected onMoveClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this._modalService.openMoveEntityModal(this.entity()).result.then((entityMoved) => {
      if (entityMoved) {
        this.entityMoved.emit(this.entity());
      }
    });
  }

  protected onShareClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    try {
      this._modalService.openShareDocument(this.entity().id);
    } catch (e) {
      this._logger.error("Failed to open share modal", e);
    }
  }

  protected async onExportClick(format: "markdown" | "document" | "pdf" | "docx"): Promise<void> {
    try {
      const doc = this.entity();
      if (!doc) return;
      const title = doc.title || "document";

      const activeDoc = this._docsStore.activeDocument?.();
      if (activeDoc && activeDoc.id === doc.id) {
        const saved = await this._docsStore.saveCurrentDocumentWithDiffResolution();
        if (!saved) {
          this._toastService.error("Could not save changes before export");
          return;
        }
      }

      // Get document content from http service
      const fullDocument = await firstValueFrom(this._documentHttpService.getDocument(doc.id));
      const content = this._documentExport.normalizeMarkdown(fullDocument.content || "");

      if (!content) {
        this._toastService.error("Nothing to export. Document is empty.");
        return;
      }

      switch (format) {
        case "markdown":
          await this._documentExport.copyToClipboard(content);
          this._toastService.success("Markdown copied to clipboard");
          break;
        case "document":
          await this._documentExport.copyTextWithFormatting(content);
          this._toastService.success("Formatted text copied to clipboard");
          break;
        case "pdf":
          await this._exportToPdf(title, content);
          break;
        case "docx":
          await this._exportToDocx(title, content);
          break;
      }
    } catch (error) {
      this._logger.error("Export failed", error);
      this._toastService.error("Export failed. Please try again.");
    }
  }

  protected async onDeleteClick(event: Event): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    const confirmed = await this._modalService.confirm(
      `Delete Document ${this.entity().title}`,
      "This action is permanent and cannot be undone.",
      "Delete"
    );

    if (confirmed) {
      this.documentDeleted.emit(this.entity());
    }
  }

  protected override getPublicEntityUrl(id: string): string {
    return DocumentationRoutesConstants.publicDocument(id);
  }

  protected onDuplicateClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    const newName = `Copy of ${this.entity().title}`;

    this._modalService
      .openPromptModal(
        {
          currentValue: newName,
          title: "Duplicate Document",
          label: "Document Name",
          confirmButtonText: "Duplicate"
        },
        {
          keyboard: true
        }
      )
      .result.then((result) => {
        if (result) {
          this.duplicateDocument.emit({ doc: this.entity(), title: result });
        }
      });
  }

  protected override async togglePublicRequest(isPublic: boolean): Promise<Observable<DocumentListItemModel>> {
    return this._documentHttpService.setPublic(this.entity().id, isPublic);
  }

  private _toFileName(title: string, ext: string): string {
    const base =
      (title || "document")
        .toString()
        .trim()
        .replace(/\s+/g, "-")
        .replace(/[^a-zA-Z0-9\-_.]/g, "")
        .replace(/-+/g, "-")
        .replace(/^\.+|\.+$/g, "")
        .slice(0, 128) || "document";
    return `${base}.${ext}`;
  }

  private async _exportToPdf(title: string, markdown: string): Promise<void> {
    try {
      this._toastService.info("Generating PDF, please wait...");

      // Use the document export service
      const blob = await this._documentExport.exportToPdf(title, markdown);

      const filename = this._toFileName(title, "pdf");
      this._downloadBlob(blob, filename);
      this._toastService.success("PDF exported successfully");
    } catch (e) {
      this._logger.error("PDF export failed", e);
      this._toastService.error("Failed to export PDF");
      throw e;
    }
  }

  private async _exportToDocx(title: string, markdown: string): Promise<void> {
    try {
      this._toastService.info("Generating DOCX document, please wait...");

      // Use the document export service
      const blob = await this._documentExport.exportToDocx(markdown, title);

      const filename = this._toFileName(title, "docx");
      this._downloadBlob(blob, filename);
      this._toastService.success("DOCX document exported successfully");
    } catch (e) {
      this._logger.error("DOCX export failed", e);
      this._toastService.error("Failed to export DOCX document");
      throw e;
    }
  }

  private _downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    try {
      link.click();
    } finally {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }
}
